import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    Box,
    Typography,
    Tooltip,
} from "@mui/material";
import {useRef} from "react";
import {HugeiconsIcon} from "@hugeicons/react";
import {InformationCircleIcon} from "@hugeicons/core-free-icons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {VariableTextField} from "../../pages/project/flow/edit-menu/components/VariableTextField";

export const MarkdownEditorModal = ({
                                        open,
                                        onClose,
                                        title = "System prompt",
                                        value,
                                        onChange,
                                        variables = [],
                                        placeholder = "The prompt text / instructions...",
                                    }) => {
    const editorBoxRef = useRef(null);
    const previewRef = useRef(null);
    // guards against the echo: setting one pane's scrollTop fires the other's
    // scroll event, which would otherwise scroll the first back and cause jitter.
    const isSyncing = useRef(false);

    const getEditor = () => editorBoxRef.current?.querySelector("textarea");

    const syncScroll = (source, target) => {
        if (!source || !target) return;
        if (isSyncing.current) {
            isSyncing.current = false;
            return;
        }
        const denom = source.scrollHeight - source.clientHeight;
        const ratio = denom > 0 ? source.scrollTop / denom : 0;
        isSyncing.current = true;
        target.scrollTop = ratio * (target.scrollHeight - target.clientHeight);
    };

    const handleEditorScroll = () => syncScroll(getEditor(), previewRef.current);
    const handlePreviewScroll = () => syncScroll(previewRef.current, getEditor());

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{fontSize: 16, fontWeight: 600}}>{title}</DialogTitle>
            <DialogContent>
                <Stack direction="row" gap={2} sx={{height: "60vh", alignItems: "stretch"}}>
                    <Box ref={editorBoxRef} sx={{flex: 1, display: "flex", flexDirection: "column", minHeight: 0}}>
                        <Stack direction="row" gap={0.5} alignItems="center" sx={{mb: 0.5}}>
                            <Typography variant="caption" fontWeight={600} color="text.secondary">
                                Editor
                            </Typography>
                            <Tooltip
                                title={
                                    <Stack gap={0.3} sx={{py: 0.5}}>
                                        <Typography variant="caption"># Heading 1 &nbsp;&nbsp;## Heading 2</Typography>
                                        <Typography variant="caption">**bold** &nbsp;&nbsp;*italic*</Typography>
                                        <Typography variant="caption">`inline code`</Typography>
                                        <Typography variant="caption">- list item &nbsp;&nbsp;1. numbered</Typography>
                                        <Typography variant="caption">[label](url)</Typography>
                                        <Typography variant="caption">&gt; blockquote</Typography>
                                        <Typography variant="caption">--- (horizontal rule)</Typography>
                                    </Stack>
                                }
                                arrow
                                placement="right"
                            >
                                <HugeiconsIcon icon={InformationCircleIcon} size={13} style={{cursor: "help", opacity: 0.6}}/>
                            </Tooltip>
                        </Stack>
                        <VariableTextField
                            autoFocus
                            fullWidth
                            multiline
                            value={value}
                            onChange={(v) => onChange({ target: { value: v } })}
                            placeholder={placeholder}
                            variables={variables}
                            inputProps={{onScroll: handleEditorScroll}}
                            InputProps={{
                                sx: {
                                    borderRadius: 0,
                                    fontFamily: "monospace",
                                    fontSize: 13,
                                    height: "100%",
                                    alignItems: "flex-start",
                                },
                            }}
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                "& .MuiInputBase-root": {height: "100%"},
                                "& textarea": {height: "100% !important", overflow: "auto !important"},
                            }}
                        />
                    </Box>

                    <Box sx={{flex: 1, display: "flex", flexDirection: "column", minHeight: 0}}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{mb: 0.5}}>
                            Preview
                        </Typography>
                        <Box
                            ref={previewRef}
                            onScroll={handlePreviewScroll}
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                overflow: "auto",
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 0,
                                p: 1.5,
                                fontSize: 13,
                            }}
                        >
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {value || "*Nothing to preview*"}
                            </ReactMarkdown>
                        </Box>
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button
                    onClick={onClose}
                    variant="modal"
                >
                    Done
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MarkdownEditorModal;
